/**
 * Timesheets / Effort Tracking — subsection rendered INSIDE the Tasks page
 * (not in the sidebar). Backed by /api/timesheets/*.
 *
 * Provides:
 *   • Daily / weekly / monthly views
 *   • Quick "Log effort" dialog linked to task + project + deliverable
 *   • Submit-week workflow (all draft entries for the chosen Mon→Sun)
 *   • Reviewer approve / reject / return actions
 *   • Per-employee performance feed surfaced for HR
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/app/ui-bits";
import AuditTrailDrawer from "@/components/app/AuditTrailDrawer";
import {
  Clock, Plus, CheckCircle, XCircle, ArrowCounterClockwise,
  PaperPlaneTilt, CalendarBlank, ChartBar,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const WORK_TYPE_LABELS = {
  design: "Design", review: "Review", site_visit: "Site Visit",
  supervision: "Supervision", engineering: "Engineering", drafting: "Drafting",
  modelling: "Modelling", meeting: "Meeting", report_writing: "Report Writing",
  admin: "Admin", training: "Training", non_billable: "Non-billable",
  leave: "Leave", other: "Other",
};

function startOfWeek(d) {
  const dt = new Date(d);
  const day = dt.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day; // Monday-based
  dt.setDate(dt.getDate() + diff);
  return dt.toISOString().slice(0, 10);
}

function NewEntryDialog({ onCreated, meta, defaultDate }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [form, setForm] = useState({
    entry_date: defaultDate || new Date().toISOString().slice(0, 10),
    hours: 1, work_type: "engineering", description: "",
    project_id: "", task_id: "", billable: true,
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (!open) return;
    api.get("/projects").then((r) => setProjects(r.data || []));
    api.get("/tasks").then((r) => setTasks(r.data?.items || r.data || []));
  }, [open]);

  const submit = async () => {
    if (!form.entry_date || !form.hours) return toast.error("Pick a date and hours.");
    setBusy(true);
    try {
      const { data } = await api.post("/timesheets", {
        ...form,
        project_id: form.project_id || null,
        task_id: form.task_id || null,
      });
      toast.success(`Logged ${data.hours}h on ${data.entry_date}`);
      onCreated?.(data);
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-primary-blue" size="sm" data-testid="ts-new-btn">
          <Plus size={14} className="mr-2" /> Log effort
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Clock size={16} /> Log effort
          </DialogTitle>
          <DialogDescription>Record hours worked against a project or task.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Date *</Label>
            <Input type="date" value={form.entry_date}
              onChange={(e) => set("entry_date", e.target.value)} data-testid="ts-date" />
          </div>
          <div><Label>Hours *</Label>
            <Input type="number" step="0.25" min="0.25" max="24" value={form.hours}
              onChange={(e) => set("hours", Number(e.target.value))} data-testid="ts-hours" />
          </div>
          <div><Label>Work type *</Label>
            <Select value={form.work_type} onValueChange={(v) => set("work_type", v)}>
              <SelectTrigger data-testid="ts-work-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(meta?.work_types || []).map((w) => (
                  <SelectItem key={w} value={w}>{WORK_TYPE_LABELS[w] || w}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Billable</Label>
            <div className="flex items-center gap-2 h-10">
              <Checkbox checked={form.billable} onCheckedChange={(v) => set("billable", !!v)} />
              <span className="text-xs">{form.billable ? "Billable" : "Non-billable"}</span>
            </div>
          </div>
          <div className="col-span-2"><Label>Project</Label>
            <Select value={form.project_id || "_any"}
              onValueChange={(v) => set("project_id", v === "_any" ? "" : v)}>
              <SelectTrigger data-testid="ts-project"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_any">No project</SelectItem>
                {projects.slice(0, 50).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Task</Label>
            <Select value={form.task_id || "_any"}
              onValueChange={(v) => set("task_id", v === "_any" ? "" : v)}>
              <SelectTrigger data-testid="ts-task"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_any">No task</SelectItem>
                {tasks.slice(0, 50).map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Description</Label>
            <Textarea rows={3} value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="What did you work on?" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="btn-primary-blue" data-testid="ts-submit">
            {busy ? "Saving…" : "Save entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TimesheetsPanel() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("mine");      // mine | team | summary
  const [scope, setScope] = useState("week");   // week | month | range
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));

  const isReviewer = meta?.is_reviewer || hasMinRole(user, "manager");
  const range = useMemo(() => {
    if (scope === "week") {
      const s = weekStart;
      const e = new Date(s); e.setDate(e.getDate() + 6);
      return { from: s, to: e.toISOString().slice(0, 10) };
    }
    if (scope === "month") {
      const d = new Date(weekStart);
      const from = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
      const to = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
      return { from, to };
    }
    return { from: weekStart, to: weekStart };
  }, [scope, weekStart]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: range.from, date_to: range.to });
      if (tab === "mine") params.set("mine", "true");
      const [m, l, s] = await Promise.all([
        api.get("/timesheets/meta"),
        api.get(`/timesheets?${params.toString()}`),
        api.get(`/timesheets/summary?date_from=${range.from}&date_to=${range.to}`),
      ]);
      setMeta(m.data);
      setItems(l.data.items || []);
      setSummary(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tab, scope, weekStart]);

  const submitWeek = async () => {
    try {
      const { data } = await api.post("/timesheets/submit-week", { week_start: weekStart });
      toast.success(`Submitted ${data.submitted} draft entr${data.submitted === 1 ? "y" : "ies"}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const review = async (id, new_status) => {
    try {
      await api.post(`/timesheets/${id}/review`, { new_status, comment: "" });
      toast.success(`Marked ${new_status}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="timesheets-panel">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900">Timesheets / Effort Tracking</h3>
          <p className="text-xs text-slate-500">
            Record hours against tasks, projects, deliverables. Submitted entries feed Projects KPIs and HR Performance.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={submitWeek} data-testid="ts-submit-week">
            <PaperPlaneTilt size={12} className="mr-1.5" /> Submit week
          </Button>
          <NewEntryDialog onCreated={load} meta={meta} defaultDate={range.from} />
        </div>
      </div>

      {/* View toggles */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
          {["mine", isReviewer ? "team" : null, "summary"].filter(Boolean).map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`ts-tab-${t}`}
              className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm transition ${
                tab === t ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}>{t === "mine" ? "My entries" : t === "team" ? "Team / Approvals" : "Summary"}</button>
          ))}
        </div>
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
          {["week", "month"].map((s) => (
            <button key={s} onClick={() => setScope(s)} data-testid={`ts-scope-${s}`}
              className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm transition ${
                scope === s ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}>{s === "week" ? "Weekly" : "Monthly"}</button>
          ))}
        </div>
        <Input type="date" value={weekStart}
          onChange={(e) => setWeekStart(scope === "week" ? startOfWeek(e.target.value) : e.target.value)}
          className="w-40" data-testid="ts-period-input" />
        <div className="font-mono-alt text-xs text-slate-500">
          <CalendarBlank size={12} className="inline mr-1" />
          {formatDate(range.from)} → {formatDate(range.to)}
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Card label="Total hours" value={`${summary.total_hours}h`} />
          <Card label="Billable" value={`${summary.billable_hours}h`} />
          <Card label="Entries" value={summary.entries} />
          <Card label="Approved" value={`${summary.by_status?.approved || 0}h`} />
        </div>
      )}

      {tab === "summary" ? (
        <SummaryView summary={summary} />
      ) : loading ? (
        <div className="py-10 text-center font-mono-alt text-xs uppercase tracking-widest text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="dc-card p-10 text-center">
          <Clock size={32} className="mx-auto text-slate-300" />
          <div className="mt-2 font-display text-sm font-semibold">No entries in this period</div>
          <div className="text-xs text-slate-500">Click "Log effort" above to add one.</div>
        </div>
      ) : (
        <div className="dc-card overflow-hidden">
          <table className="hidden w-full md:table">
            <thead className="bg-slate-50 text-left text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">Staff</th>
                <th className="px-5 py-3">Project · Task</th>
                <th className="px-5 py-3">Work type</th>
                <th className="px-5 py-3 text-right">Hours</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((e) => (
                <tr key={e.id} className="text-sm hover:bg-slate-50 transition" data-testid={`ts-row-${e.id}`}>
                  <td className="px-5 py-3 font-mono-alt text-xs">{formatDate(e.entry_date)}</td>
                  <td className="px-5 py-3 text-xs">
                    <div className="font-semibold text-slate-900">{e.user_full_name || e.user_email}</div>
                    <div className="text-[11px] text-slate-500">{e.department || "—"}</div>
                  </td>
                  <td className="px-5 py-3 text-xs">
                    <div>{e.project_name || <span className="text-slate-400">—</span>}</div>
                    <div className="text-[11px] text-slate-500">{e.task_title || ""}</div>
                  </td>
                  <td className="px-5 py-3 text-xs">{WORK_TYPE_LABELS[e.work_type] || e.work_type}{!e.billable && <span className="ml-1 rounded bg-slate-100 px-1 text-[9px] font-mono-alt uppercase">N-B</span>}</td>
                  <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{e.hours.toFixed(1)}h</td>
                  <td className="px-5 py-3"><StatusBadge status={e.status} /></td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1.5">
                      <AuditTrailDrawer entity="timesheet" entityId={e.id} label="Audit" />
                      {isReviewer && e.status === "submitted" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => review(e.id, "approved")} data-testid={`ts-approve-${e.id}`}>
                            <CheckCircle size={12} className="mr-1 text-emerald-700" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => review(e.id, "returned")} data-testid={`ts-return-${e.id}`}>
                            <ArrowCounterClockwise size={12} className="mr-1" /> Return
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => review(e.id, "rejected")} data-testid={`ts-reject-${e.id}`}>
                            <XCircle size={12} className="mr-1 text-red-700" /> Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="divide-y divide-slate-100 md:hidden">
            {items.map((e) => (
              <li key={e.id} className="p-4 text-sm" data-testid={`ts-mrow-${e.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">{e.user_full_name || e.user_email}</div>
                    <div className="text-[11px] text-slate-500">{formatDate(e.entry_date)} · {WORK_TYPE_LABELS[e.work_type] || e.work_type}</div>
                    {e.project_name && <div className="text-[11px] text-slate-500">{e.project_name}</div>}
                  </div>
                  <div className="text-right">
                    <div className="font-mono-alt font-semibold">{e.hours.toFixed(1)}h</div>
                    <StatusBadge status={e.status} />
                  </div>
                </div>
                {isReviewer && e.status === "submitted" && (
                  <div className="mt-2 flex justify-end gap-1">
                    <Button size="sm" variant="outline" onClick={() => review(e.id, "approved")}>Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => review(e.id, "rejected")}>Reject</Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Card({ label, value }) {
  return (
    <div className="dc-card p-3">
      <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-0.5 font-display text-xl font-semibold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

function SummaryView({ summary }) {
  if (!summary) return null;
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <SummaryBlock title="By project" data={summary.by_project}
        render={(k, v) => `${v.name} — ${v.hours}h`} />
      <SummaryBlock title="By staff" data={summary.by_user}
        render={(k, v) => `${v.name} — ${v.hours}h`} />
      <SummaryBlock title="By work type" data={summary.by_work_type}
        render={(k, v) => `${WORK_TYPE_LABELS[k] || k} — ${v}h`} />
      <SummaryBlock title="By status" data={summary.by_status}
        render={(k, v) => `${k} — ${v}h`} />
    </div>
  );
}
function SummaryBlock({ title, data, render }) {
  const entries = Object.entries(data || {});
  return (
    <div className="dc-card p-4">
      <div className="flex items-center gap-2 mb-2">
        <ChartBar size={14} style={{ color: "#1E5CBA" }} />
        <h4 className="font-display text-sm font-semibold">{title}</h4>
      </div>
      {entries.length === 0 ? (
        <div className="text-xs text-slate-500 italic">No data.</div>
      ) : (
        <ul className="space-y-1 text-xs">
          {entries.map(([k, v]) => <li key={k} className="text-slate-700">· {render(k, v)}</li>)}
        </ul>
      )}
    </div>
  );
}
